import {
  ArchiveIcon,
  DropdownMenu,
  Modal,
  MoreHorizIcon,
  ShareIcon,
  TableV2,
  Toggle,
  UnarchiveIcon,
} from '@medeloop/medeloop-components-library';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUserOverview } from 'src/api/users-queries';
import {
  DataSourceOrganization,
} from 'src/types/data-sources';
import { ShareDatasets } from '../share-datasets/share-datasets';
import { useUpdateDataSourceMutation } from "src/api/data-sources-queries";
import { useSelector } from 'react-redux';
import { selectIsUserAdmin } from 'src/store/auth';

export const DataSetsList = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showArchivedDatasets, setShowArchivedDatasets] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<DataSourceOrganization | null>(null);
  const [isArchiveDatasetModalOpen, setIsArchiveDatasetModalOpen] = useState(false);
  const isUserAdmin = useSelector(selectIsUserAdmin);

  const updateDataSourceMutation = useUpdateDataSourceMutation();

  const navigate = useNavigate();

  const { isLoading, data } = useGetUserOverview();

  const onRowClick = (row: DataSourceOrganization) => {
    navigate(`/account/data/datasets/${row.dataSourceId}/overview`);
  };

  const onShareButtonClick = (dataSource: DataSourceOrganization) => {
    setSelectedDataset(dataSource);
    setIsShareModalOpen(true);
  };

  const handleArchiveUnarchiveDataset = (dataSource: DataSourceOrganization) => {
    if (!dataSource?.archivedAt) {
      updateDataSourceMutation.mutate({
        dataSourceId: dataSource.dataSourceId,
        archive: true,
      });
    }
  };

  const onArchiveUnarchiveClick = (dataSource: DataSourceOrganization) => {
    if (dataSource?.archivedAt) {
      updateDataSourceMutation.mutate({
        dataSourceId: dataSource.dataSourceId,
        archive: false,
      });
    } else {
      setSelectedDataset(dataSource);
      setIsArchiveDatasetModalOpen(true);
    }
  };

  const headers = [
    { label: 'Dataset', key: 'dataset' },
    { label: 'Manager', key: 'manager' },
    { label: 'Updated', key: 'updated' },
    { label: '', key: 'actions' },
  ];

  const transformedData = useMemo(
    () =>
      data?.dataSources
        .filter(
          (dataSource) =>
            dataSource.dataSourceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dataSource.dataSourceDisplayName.toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .filter((dataSource) => {
          if (showArchivedDatasets) {
            return true;
          }
          return !dataSource.archivedAt;
        })
        .map((dataSource) => ({
          dataset: dataSource.dataSourceDisplayName,
          manager: dataSource.organizationDisplayName,
          updated: new Date(dataSource.updatedAt).toISOString().split('T')[0],
          actions: isUserAdmin ? (
            <DropdownMenu
              component={<MoreHorizIcon />}
              items={[
                {
                  name: 'Share',
                  icon: <ShareIcon />,
                  onClick: () => onShareButtonClick(dataSource),
                },
                {
                  name: dataSource.archivedAt ? 'Unarchive' : 'Archive',
                  icon: dataSource.archivedAt ? <UnarchiveIcon /> : <ArchiveIcon />,
                  onClick: () => onArchiveUnarchiveClick(dataSource),
                },
              ]}
            />
          ) : null,
          onClick: () => onRowClick(dataSource),
          className: dataSource.archivedAt ? 'bg-background-dark-subdued3' : '',
        })) ?? [],
    [data?.dataSources, searchTerm, showArchivedDatasets],
  );

  return (
    <div className="flex flex-col gap-6 mt-6">
      <TableV2
        headers={headers}
        data={transformedData}
        isLoading={isLoading}
        searchPlaceholder="Search datasets"
        onSearch={(t) => setSearchTerm(t)}
        tableActions={isUserAdmin ? (
          <div className="flex items-center">
            <Toggle
              value={showArchivedDatasets}
              label="Show archived datasets"
              onChange={() => setShowArchivedDatasets((prev) => !prev)}
            />
          </div>) : null
          }
        withDivider={false}
      />
      {isArchiveDatasetModalOpen && selectedDataset && (
        <Modal
          actionType="primary"
          actionText="Archive dataset"
          actionFunction={() => handleArchiveUnarchiveDataset(selectedDataset)}
          isOpen={isArchiveDatasetModalOpen}
          onOpenChange={() => {
            setSelectedDataset(null);
            setIsArchiveDatasetModalOpen(false);
          }}
          title="Archive dataset"
        >
          <div className="flex flex-col gap-6 w-96">
            <div>
              Archiving this dataset will temporarily disable access to its data for analysis and
              reporting. <br /> You can unarchive it at any time.
            </div>
            <div>Are you sure you want to archive {selectedDataset.dataSourceDisplayName}?</div>
          </div>
        </Modal>
      )}

      <ShareDatasets
        isShareModalOpen={isShareModalOpen}
        setIsShareModalOpen={setIsShareModalOpen}
        dataset={selectedDataset}
      />
    </div>
  );
};
